import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, MessageCircle, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { CTABand, Reveal } from "@/components/ui-bits";
import { useProduct, useProducts } from "@/hooks/useContent";
import { waLink, track } from "@/lib/site";

function Section({ title, children }) {
  return (
    <Reveal>
      <div className="border-t border-line py-8" data-testid={`product-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <h2 className="font-serif text-xl font-semibold text-navy sm:text-2xl">{title}</h2>
        <div className="mt-4 text-sm leading-relaxed text-ink/85">{children}</div>
      </div>
    </Reveal>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { data: product, error, isLoading } = useProduct(slug);
  const { data: allProducts } = useProducts();
  const related = (allProducts || []).filter((p) => p.slug !== slug && p.category_slug === product?.category_slug).slice(0, 3);
  const [photoIdx, setPhotoIdx] = useState(0);
  useEffect(() => setPhotoIdx(0), [slug]);

  if (isLoading) {
    return <div className="container-x py-24"><div className="card h-96 animate-pulse" /></div>;
  }
  if (error || !product) {
    return (
      <div className="container-x py-24 text-center">
        <h1 className="font-serif text-3xl font-semibold text-navy">Product not found</h1>
        <p className="mt-3 text-mist">The product you are looking for may have been renamed or removed.</p>
        <Link to="/products" className="btn-gold mt-6">Browse All Products</Link>
      </div>
    );
  }

  const specs = Object.entries(product.specifications || {});
  const images = [product.image, ...(product.gallery || [])].filter(Boolean);
  const activeImg = images[Math.min(photoIdx, images.length - 1)] || product.image;
  const productJsonld = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.short_description || product.seo_description,
    category: product.category_slug,
    brand: { "@type": "Brand", name: "Orvella Overseas Private Limited" },
    manufacturer: { "@type": "Organization", name: "Orvella Overseas Private Limited" },
  };
  const breadcrumbJsonld = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.orvellaoverseas.com/" },
      { "@type": "ListItem", position: 2, name: "Products", item: "https://www.orvellaoverseas.com/products" },
      { "@type": "ListItem", position: 3, name: product.name, item: `https://www.orvellaoverseas.com/products/${product.slug}` },
    ],
  };

  return (
    <>
      <Seo
        title={product.seo_title || `${product.name} Exporter from India | Orvella Overseas`}
        description={product.seo_description || product.short_description}
        path={`/products/${product.slug}`}
        image={product.image}
        jsonld={[productJsonld, breadcrumbJsonld]}
      />

      <div className="border-b border-line bg-sand">
        <nav className="container-x flex items-center gap-1.5 py-3 text-xs text-mist" aria-label="Breadcrumb" data-testid="product-breadcrumb">
          <Link to="/" className="hover:text-gold-dark">Home</Link>
          <ChevronRight size={12} />
          <Link to="/products" className="hover:text-gold-dark">Products</Link>
          <ChevronRight size={12} />
          <span className="font-semibold text-navy">{product.name}</span>
        </nav>
      </div>

      <section className="border-b border-line bg-white">
        <div className="container-x grid gap-10 py-10 md:py-14 lg:grid-cols-2">
          <Reveal className="min-w-0">
            <div className="overflow-hidden rounded-xl border border-line bg-sand">
              <img src={activeImg} alt={`${product.name} photo ${Math.min(photoIdx, images.length - 1) + 1} — supplied from India by Orvella Overseas`} className="aspect-[4/3] w-full object-cover" loading="eager" fetchpriority="high" />
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="product-gallery-thumbs">
                {images.map((src, i) => (
                  <button
                    key={`${src}-${i}`}
                    type="button"
                    onClick={() => setPhotoIdx(i)}
                    data-testid={`product-thumb-${i}`}
                    aria-label={`Show photo ${i + 1} of ${product.name}`}
                    className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${i === photoIdx ? "border-gold" : "border-transparent hover:border-gold/40"}`}
                  >
                    <img src={src} alt={`${product.name} thumbnail ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
            {product.video_url && (
              <video
                src={product.video_url}
                controls
                preload="metadata"
                data-testid="product-video"
                aria-label={`${product.name} product video`}
                className="mt-3 w-full rounded-xl border border-line bg-navy"
              />
            )}
          </Reveal>
          <Reveal delay={0.1} className="flex min-w-0 flex-col justify-center">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold-dark">
              {(product.category_slug || "").replace(/-/g, " ")}
            </span>
            <h1 className="mt-3 font-serif text-3xl font-semibold leading-tight text-navy sm:text-4xl">{product.name}</h1>
            <p className="mt-4 text-[15px] leading-relaxed text-mist">{product.short_description}</p>
            <p className="mt-4 inline-flex w-fit items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-gold-dark">
              Available for International B2B Supply
            </p>
            <div className="mt-7 flex flex-wrap gap-3" data-testid="product-cta-row">
              <Link to={`/quote?product=${encodeURIComponent(product.name)}`} data-testid="product-request-quote-btn" onClick={() => track("quote_open", { product: product.name })} className="btn-gold">
                Request a Quote
              </Link>
              <Link to={`/quote?type=sample&product=${encodeURIComponent(product.name)}`} data-testid="product-request-sample-btn" onClick={() => track("sample_open", { product: product.name })} className="btn-navy">
                Request a Sample
              </Link>
              <a
                href={waLink(`Hello Orvella Overseas, I am interested in sourcing ${product.name} from India. I would like to discuss my requirements.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-whatsapp-btn"
                onClick={() => track("whatsapp_click", { product: product.name })}
                className="btn-outline"
              >
                <MessageCircle size={16} /> WhatsApp Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container-x max-w-4xl pb-4">
        <Section title="Product Overview">
          <p>{product.description}</p>
          {product.description_more && (
            <p className="mt-4 whitespace-pre-line border-t border-line pt-4 text-ink/85" data-testid="product-description-more">{product.description_more}</p>
          )}
        </Section>

        {specs.length > 0 && (
          <Section title="Specifications">
            <div className="overflow-hidden rounded-lg border border-line">
              <table className="w-full text-left text-sm" data-testid="product-specifications-table">
                <tbody>
                  {specs.map(([k, v], i) => (
                    <tr key={k} className={i % 2 ? "bg-white" : "bg-sand/60"}>
                      <th className="w-1/3 px-4 py-3 align-top font-bold text-navy">{k}</th>
                      <td className={`px-4 py-3 ${v === "[TO BE UPDATED]" || v === "Confirmed per enquiry" ? "text-mist italic" : "text-ink"}`}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-mist">
              Specifications are finalized with the buyer for each order. Values marked as pending are confirmed by our export team on enquiry.
            </p>
          </Section>
        )}

        {(product.packaging || product.moq) && (
          <Section title="Packaging">
            <p>{product.packaging}</p>
            <p className="mt-2"><strong className="text-navy">Minimum Order Quantity:</strong> {product.moq}</p>
            {product.storage_conditions && <p className="mt-2"><strong className="text-navy">Storage Conditions:</strong> {product.storage_conditions}</p>}
          </Section>
        )}

        <Section title="Quality & Compliance">
          <p>
            Product quality is discussed and agreed with the buyer before order confirmation. Our quality approach covers supplier
            selection, agreed specifications, quality checks during preparation, packaging checks and documentation coordinated as
            applicable to the shipment. Any certificates or test documents applicable to your order can be discussed with our export team.
          </p>
        </Section>

        {(product.applications || []).length > 0 && (
          <Section title="Applications">
            <ul className="list-inside list-disc space-y-1">
              {product.applications.filter((a) => a && a !== "[TO BE UPDATED]").map((a) => <li key={a}>{a}</li>)}
              {product.applications.every((a) => a === "[TO BE UPDATED]") && (
                <li className="text-mist italic">Applications depend on the product form and buyer requirement — discuss your use case with our export team.</li>
              )}
            </ul>
          </Section>
        )}

        <Section title="Export / Logistics">
          <p>{product.export_logistics}</p>
          <p className="mt-2"><strong className="text-navy">Origin:</strong> {product.origin}</p>
          {product.hs_code && product.hs_code !== "[TO BE UPDATED]" && (
            <p className="mt-2"><strong className="text-navy">HS Code:</strong> {product.hs_code}</p>
          )}
          <Link to="/export-process" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gold-dark hover:text-navy">
            See our export process <ArrowRight size={14} />
          </Link>
        </Section>

        {(product.faqs || []).length > 0 && (
          <Section title="FAQ">
            <div className="space-y-3">
              {product.faqs.map((f) => (
                <details key={f.question} className="card px-5 py-4">
                  <summary className="cursor-pointer list-none text-sm font-bold text-navy [&::-webkit-details-marker]:hidden">
                    <span className="mr-2 text-gold-dark">+</span>{f.question}
                  </summary>
                  <p className="mt-3 border-t border-line pt-3 text-sm leading-relaxed text-mist">{f.answer}</p>
                </details>
              ))}
            </div>
          </Section>
        )}
      </section>

      {related.length > 0 && (
        <section className="section-pad bg-sand">
          <div className="container-x">
            <h2 className="font-serif text-2xl font-semibold text-navy">Related Products</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      <CTABand
        title={`Interested in sourcing ${product.name} from India?`}
        sub="Share your quantity, packaging and destination details — our export team will respond with specifications and a commercial quotation."
      />
    </>
  );
}
